import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/stores/auth';

const S = {
  font: "'Sora', system-ui, sans-serif",
  heading: "'Fraunces', Georgia, serif",
};

export default function ExamCheckoutPage() {
  const { examId } = useParams();
  const [searchParams] = useSearchParams();
  const initialType = (searchParams.get('type') as 'computer' | 'paper') || 'computer';
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { user, isAuthenticated } = useAuth();

  const [step, setStep] = useState(1);
  const [examType, setExamType] = useState<'computer' | 'paper'>(initialType);
  const [firstName, setFirstName] = useState(user?.name?.split(' ')[0] || '');
  const [lastName, setLastName] = useState(user?.name?.split(' ').slice(1).join(' ') || '');
  const [email, setEmail] = useState(user?.email || '');
  const [canAttendBaghdad, setCanAttendBaghdad] = useState(false);
  const [examDate, setExamDate] = useState('');
  const [examTime, setExamTime] = useState('');
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => { if (!isAuthenticated) navigate('/login'); }, [isAuthenticated, navigate]);

  const { data: exams } = useQuery({
    queryKey: ['public-exams'],
    queryFn: async () => { const res = await fetch('/api/exams'); if (!res.ok) throw new Error('Failed'); return res.json(); },
  });

  const exam = exams?.find((e: any) => e.id === examId);
  const price = exam ? (examType === 'computer' ? exam.computerPrice : exam.paperPrice) : 0;
  const minDate = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const validateForm = () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim()) { setError('Please fill in all required fields'); return false; }
    if (examType === 'paper') {
      if (!canAttendBaghdad) { setError('You must confirm attendance in Baghdad for paper-based exam'); return false; }
      if (!examDate) { setError('Please select an exam date'); return false; }
      if (!examTime) { setError('Please select an exam time'); return false; }
      const days = Math.ceil((new Date(examDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      if (days < 5) { setError('Exam date must be at least 5 days in advance'); return false; }
    }
    setError(''); return true;
  };

  const handleNext = () => { if (validateForm()) setStep(2); };

  const handlePayment = async () => {
    setError(''); setIsProcessing(true);
    try {
      const res = await fetch('/api/payments/exam-initiate', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          examProductId: examId, examType, locale: 'en', firstName, lastName, email,
          canAttendBaghdad: examType === 'paper' ? canAttendBaghdad : undefined,
          preferredDate: examType === 'paper' ? examDate : undefined,
          preferredTime: examType === 'paper' ? examTime : undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) { setError(data.error || 'Payment failed'); setIsProcessing(false); return; }
      sessionStorage.setItem('examOrderId', data.data.orderId);
      if (data.data.redirectUrl) window.location.href = data.data.redirectUrl;
    } catch { setError('Something went wrong. Please try again.'); setIsProcessing(false); }
  };

  if (!isAuthenticated || !exam) return null;

  const inputStyle: React.CSSProperties = {
    width: '100%', fontFamily: S.font, fontSize: 14, padding: '12px 16px',
    borderRadius: 12, border: '1.5px solid #d4c9a8', outline: 'none', background: '#fffef9',
  };
  const labelStyle: React.CSSProperties = {
    fontFamily: S.font, fontSize: 13, fontWeight: 600, color: '#3d3929', display: 'block', marginBottom: 8,
  };
  const sectionStyle: React.CSSProperties = {
    background: '#fffef9', borderRadius: 16, border: '1.5px solid #d4c9a8', padding: '22px 24px', marginBottom: 20,
  };

  return (
    <div style={{ background: 'linear-gradient(180deg, #faf6ec 0%, #f5efe0 100%)', minHeight: '100vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 20px' }}>
      <div style={{ background: '#ffffff', borderRadius: 24, border: '1.5px solid #d4c9a8', boxShadow: '0 8px 40px rgba(139,119,62,0.08)', maxWidth: 580, width: '100%', overflow: 'hidden' }}>
        <div style={{ height: 5, background: 'linear-gradient(90deg, #b8860b, #d4a843, #b8860b)' }} />

        {/* Progress */}
        <div style={{ padding: '24px 32px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: S.font, fontSize: 12, fontWeight: 700, background: '#b8860b', color: '#fff' }}>1</div>
            <span style={{ fontFamily: S.font, fontSize: 13, fontWeight: 600, color: '#3d3929' }}>{isRTL ? "التفاصيل" : "Details"}</span>
          </div>
          <div style={{ flex: 1, height: 2, background: step >= 2 ? '#b8860b' : '#d4c9a8', borderRadius: 1 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: S.font, fontSize: 12, fontWeight: 700, background: step >= 2 ? '#b8860b' : '#e8dfc8', color: step >= 2 ? '#fff' : '#8a7d5a' }}>2</div>
            <span style={{ fontFamily: S.font, fontSize: 13, fontWeight: 600, color: step >= 2 ? '#3d3929' : '#a09478' }}>{isRTL ? "الدفع" : "Payment"}</span>
          </div>
        </div>

        <div style={{ padding: '28px 32px 36px' }}>
          <button onClick={() => step === 2 ? setStep(1) : navigate(-1)} style={{ fontFamily: S.font, fontSize: 13, color: '#8a7d5a', background: 'none', border: 'none', cursor: 'pointer', marginBottom: 24, padding: 0 }}>
            ← {step === 2 ? (isRTL ? 'العودة للتفاصيل' : 'Back to details') : (isRTL ? 'رجوع' : 'Back')}
          </button>

          {step === 1 && (<>
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: 'inline-block', fontFamily: S.font, fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', color: '#8a6914', background: '#f5ecd0', padding: '4px 12px', borderRadius: 20, marginBottom: 12 }}>{isRTL ? "معتمد من PeopleCert" : "PEOPLECERT CERTIFIED"}</div>
              <h2 style={{ fontFamily: S.heading, fontSize: 28, fontWeight: 600, color: '#2c2517', marginBottom: 6 }}>{isRTL ? "التسجيل في الامتحان" : "Exam Registration"}</h2>
              <p style={{ fontFamily: S.font, fontSize: 14, color: '#8a7d5a', lineHeight: 1.5 }}>Fill in your details for the <strong>{exam.titleEn}</strong></p>
            </div>

            <div style={sectionStyle}>
              <label style={{ ...labelStyle, marginBottom: 14, fontSize: 14 }}>{isRTL ? "نوع الامتحان" : "Exam Type"}</label>
              <div style={{ display: 'flex', gap: 12 }}>
                {(['computer', 'paper'] as const).filter(t => t === 'computer' || exam.paperPrice != null).map(t => {
                  const active = examType === t;
                  return (
                    <div key={t} onClick={() => setExamType(t)} style={{ flex: 1, padding: '16px 18px', borderRadius: 14, cursor: 'pointer', border: '2px solid ' + (active ? '#b8860b' : '#d4c9a8'), background: active ? 'linear-gradient(135deg, #fdf6e3, #f9edcc)' : '#fff', position: 'relative' as const }}>
                      {active && <div style={{ position: 'absolute' as const, top: 10, right: 12, width: 18, height: 18, borderRadius: '50%', background: '#b8860b', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700 }}>✓</div>}
                      <div style={{ fontFamily: S.font, fontSize: 14, fontWeight: 700, color: '#2c2517', marginBottom: 3 }}>{t === 'computer' ? '💻 Computer-Based' : '📝 Paper-Based'}</div>
                      <div style={{ fontFamily: S.font, fontSize: 12, color: '#8a7d5a', marginBottom: 8 }}>{t === 'computer' ? 'Online from anywhere' : 'In-person in Baghdad'}</div>
                      <div style={{ fontFamily: S.heading, fontSize: 18, fontWeight: 700, color: '#2c2517' }}>{(t === 'computer' ? exam.computerPrice : exam.paperPrice)?.toLocaleString()}<span style={{ fontSize: 11, color: '#8a7d5a', fontWeight: 400, marginLeft: 4 }}>IQD</span></div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={sectionStyle}>
              <label style={{ ...labelStyle, marginBottom: 14, fontSize: 14 }}>{isRTL ? "المعلومات الشخصية" : "Personal Information"}</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ ...labelStyle, fontSize: 12, fontWeight: 500 }}>{isRTL ? "الاسم الأول *" : "First Name *"}</label>
                  <input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First name" style={inputStyle} />
                </div>
                <div>
                  <label style={{ ...labelStyle, fontSize: 12, fontWeight: 500 }}>{isRTL ? "اسم العائلة *" : "Last Name *"}</label>
                  <input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last name" style={inputStyle} />
                </div>
              </div>
              <div>
                <label style={{ ...labelStyle, fontSize: 12, fontWeight: 500 }}>{isRTL ? "البريد الإلكتروني *" : "Email Address *"}</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@example.com" style={inputStyle} />
              </div>
            </div>

            {examType === 'paper' && (
              <div style={{ ...sectionStyle, background: '#fdf8ef', borderColor: '#c9b06b' }}>
                <label style={{ ...labelStyle, marginBottom: 14, fontSize: 14 }}>📍 Paper-Based Exam Details</label>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16, padding: '12px 14px', background: '#fff', borderRadius: 10, border: '1px solid #d4c9a8' }}>
                  <input type="checkbox" checked={canAttendBaghdad} onChange={e => setCanAttendBaghdad(e.target.checked)} style={{ marginTop: 2, accentColor: '#b8860b' }} />
                  <span style={{ fontFamily: S.font, fontSize: 13, color: '#3d3929', lineHeight: 1.4 }}>I confirm that I can attend the exam in <strong>{isRTL ? "بغداد، العراق" : "Baghdad, Iraq"}</strong></span>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ ...labelStyle, fontSize: 12, fontWeight: 500 }}>{isRTL ? "التاريخ المفضل *" : "Preferred Date *"} (at least 5 days in advance)</label>
                  <input type="date" value={examDate} onChange={e => setExamDate(e.target.value)} min={minDate} style={inputStyle} />
                </div>
                <div>
                  <label style={{ ...labelStyle, fontSize: 12, fontWeight: 500 }}>{isRTL ? "الوقت المفضل *" : "Preferred Time *"}</label>
                  <select value={examTime} onChange={e => setExamTime(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="">{isRTL ? "اختر وقت الامتحان" : "Select exam time"}</option>
                    <option value="15:00-17:00">15:00 - 17:00 (3 PM - 5 PM)</option>
                    {examDate && (new Date(examDate).getDay() === 5 || new Date(examDate).getDay() === 6) && (
                      <option value="12:00-17:00">12:00 - 17:00 (12 PM - 5 PM)</option>
                    )}
                  </select>
                </div>
              </div>
            )}

            {error && (<div style={{ background: '#fef2f2', border: '1.5px solid #fecaca', color: '#dc2626', borderRadius: 14, padding: '12px 16px', fontFamily: S.font, fontSize: 13, marginBottom: 16 }}>⚠️ {error}</div>)}

            <button onClick={handleNext} style={{ width: '100%', fontFamily: S.font, fontSize: 15, fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg, #b8860b, #9a7209)', padding: '16px 0', borderRadius: 50, border: 'none', cursor: 'pointer', boxShadow: '0 4px 16px rgba(184,134,11,0.25)' }}>
              {isRTL ? '← المتابعة للدفع' : 'Continue to Payment →'}
            </button>
          </>)}

          {step === 2 && (<>
            <div style={{ marginBottom: 28 }}>
              <h2 style={{ fontFamily: S.heading, fontSize: 28, fontWeight: 600, color: '#2c2517', marginBottom: 6 }}>{isRTL ? "مراجعة ودفع" : "Review & Pay"}</h2>
              <p style={{ fontFamily: S.font, fontSize: 14, color: '#8a7d5a' }}>Confirm your details and complete payment</p>
            </div>

            <div style={sectionStyle}>
              <div style={{ display: 'inline-block', fontFamily: S.font, fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: '#8a6914', background: '#f5ecd0', padding: '3px 10px', borderRadius: 20, marginBottom: 12 }}>PEOPLECERT</div>
              <h3 style={{ fontFamily: S.heading, fontSize: 18, fontWeight: 600, color: '#2c2517', marginBottom: 16 }}>{exam.titleEn}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', fontFamily: S.font, fontSize: 13 }}>
                <div><span style={{ color: '#8a7d5a', fontSize: 11, display: 'block', marginBottom: 2 }}>Full Name</span><strong style={{ color: '#2c2517' }}>{firstName} {lastName}</strong></div>
                <div><span style={{ color: '#8a7d5a', fontSize: 11, display: 'block', marginBottom: 2 }}>Email</span><strong style={{ color: '#2c2517' }}>{email}</strong></div>
                <div><span style={{ color: '#8a7d5a', fontSize: 11, display: 'block', marginBottom: 2 }}>Exam Type</span><strong style={{ color: '#2c2517' }}>{examType === 'computer' ? '💻 Computer-Based' : '📝 Paper-Based'}</strong></div>
                {examType === 'paper' && examDate && (<div><span style={{ color: '#8a7d5a', fontSize: 11, display: 'block', marginBottom: 2 }}>Preferred Date & Time</span><strong style={{ color: '#2c2517' }}>{examDate} · {examTime}</strong></div>)}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', background: '#fdf8ef', borderRadius: 14, border: '1.5px solid #d4c9a8', marginBottom: 24 }}>
              <span style={{ fontFamily: S.font, fontSize: 15, fontWeight: 600, color: '#3d3929' }}>Total</span>
              <span style={{ fontFamily: S.heading, fontSize: 28, fontWeight: 700, color: '#8a6914' }}>{price?.toLocaleString()} <span style={{ fontSize: 13, fontWeight: 400, color: '#8a7d5a' }}>IQD</span></span>
            </div>

            {error && (<div style={{ background: '#fef2f2', border: '1.5px solid #fecaca', color: '#dc2626', borderRadius: 14, padding: '12px 16px', fontFamily: S.font, fontSize: 13, marginBottom: 16 }}>⚠️ {error}</div>)}

            <button onClick={handlePayment} disabled={isProcessing} style={{ width: '100%', fontFamily: S.font, fontSize: 15, fontWeight: 700, color: '#fff', background: isProcessing ? '#999' : 'linear-gradient(135deg, #b8860b, #9a7209)', padding: '16px 0', borderRadius: 50, border: 'none', cursor: isProcessing ? 'not-allowed' : 'pointer', boxShadow: isProcessing ? 'none' : '0 4px 16px rgba(184,134,11,0.25)' }}>
              {isProcessing ? (isRTL ? 'جاري المعالجة...' : 'Processing...') : (isRTL ? 'ادفع ' + price?.toLocaleString() + ' IQD' : 'Pay ' + price?.toLocaleString() + ' IQD')}
            </button>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 18, fontFamily: S.font, fontSize: 12, color: '#8a7d5a' }}>🔒 Secure payment powered by Qi Card</div>
          </>)}
        </div>
      </div>
    </div>
  );
}
